import {FormSubmitHandler} from 'platform/components';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  useGetTireWidthQuery,
  usePatchTireWidthMutation,
  usePostTireWidthMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {TireDictionaryForm} from '../../components/TireDictionaryForm/TireDictionaryForm';
import {TireDictionaryFormType} from '../../types/TireDictionaryFormType';

export function TireInventoryWidthDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data, isLoading, isError} = useGetTireWidthQuery(
    {tireWidthId: id ?? ''},
    {skip: isNil(id)}
  );
  const [postTireWidth] = usePostTireWidthMutation();
  const [patchTireWidth] = usePatchTireWidthMutation();

  const handleSubmit: FormSubmitHandler<TireDictionaryFormType> = async (
    formData,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {
      name: formData.name,
    };

    if (isNil(id)) {
      return await postTireWidth({
        body,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.tireWidth);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await patchTireWidth({
      tireWidthId: id,
      body,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.tireWidth);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <SettingsTemplate
      header={{
        title: data?.name ?? i18n.t('entity.tiresInventory.labels.newTireWidth'),
        breadcrumbs: [
          {
            label: i18n.t('module.tiresInventory.title'),
            isCurrentPage: true,
          },
          {
            label: i18n.t('entity.tiresInventory.labels.tireWidth'),
            href: settingsRoutes.tireWidth,
          },
        ],
      }}
      isLoading={isLoading}
      isError={isError}
      isCreating={isNil(id)}
      data-testid={testIds.settings.tireWidthDetail('template')}
    >
      <TireDictionaryForm
        onSubmit={handleSubmit}
        defaultValues={{name: data?.name}}
        onDiscard={(navigateWithoutBlocker) => navigateWithoutBlocker(settingsRoutes.tireWidth)}
        data-testid={testIds.settings.tireWidthDetail('form')}
      />
    </SettingsTemplate>
  );
}
